import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { TESTIMONIALS } from "../../data/content";
import { HOME } from "../../constants/testIds";

const INTERVAL = 5000;

export default function TestimonialsCarousel() {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (paused) return;
    timer.current = setInterval(
      () => setI((v) => (v + 1) % TESTIMONIALS.length),
      INTERVAL
    );
    return () => clearInterval(timer.current);
  }, [paused]);

  const t = TESTIMONIALS[i];

  return (
    <section className="relative bg-[#FDFBF6] py-24 md:py-32">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="text-center mb-14">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961]">Words from the rooms</div>
          <h2 className="mt-6 font-serif font-light text-4xl md:text-5xl leading-[1.05] text-[#3B1F1A]">
            Guests remember two things — <em className="italic text-[#C9A961]">how they felt</em>, and what they ate.
          </h2>
        </div>

        <div
          data-testid={HOME.testimonialsCarousel}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          className="relative"
        >
          <div className="relative min-h-[280px] md:min-h-[240px]">
            <AnimatePresence mode="wait">
              <motion.figure
                key={i}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -24 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="peach-gradient border border-[#C9A961]/40 p-8 md:p-14 relative"
              >
                <Quote size={28} className="text-[#C9A961]" />
                <blockquote className="mt-6 font-serif text-xl md:text-2xl leading-snug text-[#3B1F1A] max-w-4xl">
                  &ldquo;{t.quote}&rdquo;
                </blockquote>
                <figcaption className="mt-8 pt-6 border-t border-[#3B1F1A]/15 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                  <div>
                    <div
                      className="text-sm text-[#3B1F1A] tracking-[0.14em]"
                      style={{ fontVariant: "small-caps", fontFeatureSettings: '"smcp"' }}
                    >
                      {t.author}
                    </div>
                    <div className="text-xs text-[#3B1F1A]/65 mt-1">{t.role}</div>
                  </div>
                  {t.tag && (
                    <span className="inline-flex self-start sm:self-end items-center h-7 px-3 rounded-full bg-[#3B1F1A] text-[#FDFBF6] text-[10px] uppercase tracking-[0.24em]">
                      {t.tag}
                    </span>
                  )}
                </figcaption>
              </motion.figure>
            </AnimatePresence>
          </div>

          <div className="mt-8 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {TESTIMONIALS.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setI(idx)}
                  aria-label={`Show testimonial ${idx + 1}`}
                  className={`h-[3px] transition-[width,background-color] duration-300 ${
                    idx === i ? "w-10 bg-[#C9A961]" : "w-6 bg-[#3B1F1A]/20 hover:bg-[#3B1F1A]/40"
                  }`}
                />
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid={HOME.testimonialPrev}
                onClick={() => setI((v) => (v - 1 + TESTIMONIALS.length) % TESTIMONIALS.length)}
                aria-label="Previous"
                className="h-10 w-10 rounded-full border border-[#3B1F1A]/20 flex items-center justify-center text-[#3B1F1A] hover:bg-[#3B1F1A] hover:text-[#FDFBF6] transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                data-testid={HOME.testimonialNext}
                onClick={() => setI((v) => (v + 1) % TESTIMONIALS.length)}
                aria-label="Next"
                className="h-10 w-10 rounded-full border border-[#3B1F1A]/20 flex items-center justify-center text-[#3B1F1A] hover:bg-[#3B1F1A] hover:text-[#FDFBF6] transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
